import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  MapPin, 
  Clock, 
  Phone,
  Navigation,
  Car,
  Loader2
} from "lucide-react";

const LocationPage = () => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLocation();
  }, []);

  const fetchLocation = async () => {
    try {
      const response = await axios.get(`${API}/location`);
      setLocation(response.data);
    } catch (error) {
      console.error("Error fetching location:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#374B67]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69C7EA]/20 rounded-full mb-4">
            <MapPin className="w-8 h-8 text-[#374B67]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#374B67] mb-2" data-testid="location-title">
            Localización
          </h1>
          <p className="text-slate-600">
            Encuentra el Punto Limpio de Majadahonda
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Map */}
          <Card className="overflow-hidden border-0 shadow-lg">
            <div className="aspect-square lg:aspect-auto lg:h-full min-h-[400px] bg-slate-200">
              <iframe
                title="Ubicación Punto Limpio Majadahonda"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3036.5!2d-3.8714!3d40.4586!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd418c3a0e0e0e0d%3A0x0!2sCarretera%20de%20El%20Plant%C3%ADo%2C%2047%2C%2028221%20Majadahonda%2C%20Madrid!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </Card>

          {/* Info Cards */}
          <div className="space-y-6">
            {/* Address Card */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#374B67] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-[#374B67] mb-1">
                      {location?.name || "Punto Limpio de Majadahonda"}
                    </h2>
                    <p className="text-slate-600 mb-2">
                      {location?.address || "Carretera de El Plantío, 47"}
                    </p>
                    <p className="text-slate-500">
                      {location?.postal_code || "28221"} {location?.city || "Majadahonda, Madrid"}
                    </p>
                    <Badge className="mt-3 bg-green-100 text-green-700">
                      Gestionado por {location?.managed_by || "Valoriza Servicios Medioambientales"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Schedule Card */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#69C7EA] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-[#374B67] mb-4">Horario</h2>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Lunes a Viernes</span>
                        <span className="font-medium text-[#374B67]">15:00 - 20:00</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Sábados</span>
                        <span className="font-medium text-[#374B67]">08:00 - 20:00</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Domingos y festivos</span>
                        <span className="font-medium text-[#374B67]">09:00 - 14:00</span>
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-600 font-medium">
                        Cerrado: 25 de diciembre, 1 de enero, 6 de enero
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Access Info */}
            <Card className="border-0 shadow-md bg-gradient-to-br from-slate-50 to-blue-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2E7D32] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-[#374B67] mb-2">Acceso con vehículo</h2>
                    <p className="text-slate-600">
                      Puedes acceder en tu vehículo al Punto Limpio. Solo permanece el tiempo 
                      necesario para la carga y descarga de productos.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Google Maps Link */}
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location?.address + ", " + location?.city || "Carretera de El Plantío, 47, Majadahonda")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-4 bg-[#374B67] text-white rounded-full font-semibold hover:bg-[#2C3E56] transition-colors"
              data-testid="directions-btn"
            >
              <Navigation className="w-5 h-5" />
              Cómo llegar
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LocationPage;
